import { useState, useEffect, useRef } from 'react';

/**
 * 信号雷达可视化组件
 * 展示外部信号源的实时状态和融合效果
 */
export default function SignalRadar({ signals = [], compact = false }) {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const [rotation, setRotation] = useState(0);

  // 动画旋转
  useEffect(() => {
    let start = null;
    const animate = (ts) => {
      if (!start) start = ts;
      setRotation(((ts - start) / 50) % 360);
      animRef.current = requestAnimationFrame(animate);
    };
    animRef.current = requestAnimationFrame(animate);
    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, []);

  // 绘制雷达图
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const W = canvas.width = canvas.offsetWidth * 2;
    const H = canvas.height = canvas.offsetHeight * 2;
    ctx.scale(2, 2);
    const w = W / 2, h = H / 2;
    const cx = w / 2, cy = h / 2;
    const radius = Math.min(cx, cy) - 40;

    ctx.clearRect(0, 0, w, h);

    // 背景圆环
    ctx.strokeStyle = 'rgba(255,255,255,0.04)';
    ctx.lineWidth = 1;
    for (let i = 1; i <= 4; i++) {
      ctx.beginPath();
      ctx.arc(cx, cy, radius * (i / 4), 0, Math.PI * 2);
      ctx.stroke();
    }

    // 十字线
    ctx.strokeStyle = 'rgba(255,255,255,0.03)';
    ctx.beginPath();
    ctx.moveTo(cx - radius, cy); ctx.lineTo(cx + radius, cy);
    ctx.moveTo(cx, cy - radius); ctx.lineTo(cx, cy + radius);
    ctx.stroke();

    // 扫描线
    const grad = ctx.createConicalGradient
      ? null // not widely supported
      : (() => {
          const angle = (rotation * Math.PI) / 180;
          const gx = cx + Math.cos(angle) * radius;
          const gy = cy + Math.sin(angle) * radius;
          const g = ctx.createLinearGradient(cx, cy, gx, gy);
          g.addColorStop(0, 'rgba(232,200,90,0.15)');
          g.addColorStop(1, 'rgba(232,200,90,0)');
          return g;
        })();

    if (grad) {
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.arc(cx, cy, radius, -0.5 + (rotation * Math.PI) / 180, 0.5 + (rotation * Math.PI) / 180);
      ctx.closePath();
      ctx.fill();
    }

    // 信号点
    const signalTypes = ['WEATHER', 'HOLIDAY', 'EVENT', 'CALENDAR', 'SEARCH_TREND'];
    const colors = {
      WEATHER: '#E8C85A',
      HOLIDAY: '#69B89A',
      EVENT: '#A68BD9',
      CALENDAR: '#8EB9D6',
      SEARCH_TREND: '#D48BA0',
    };

    signals.forEach((signal, i) => {
      const typeIdx = signalTypes.indexOf(signal.signalType);
      if (typeIdx === -1) return;
      const angle = ((typeIdx / signalTypes.length) * Math.PI * 2) - Math.PI / 2;
      const dist = radius * (signal.intensity / 100) * 0.85;
      const sx = cx + Math.cos(angle) * dist;
      const sy = cy + Math.sin(angle) * dist;
      const r = 4 + (signal.intensity / 100) * 6;

      // 光晕
      ctx.fillStyle = `${colors[signal.signalType] || '#E8C85A'}15`;
      ctx.beginPath();
      ctx.arc(sx, sy, r * 2, 0, Math.PI * 2);
      ctx.fill();

      // 核心点
      ctx.fillStyle = colors[signal.signalType] || '#E8C85A';
      ctx.beginPath();
      ctx.arc(sx, sy, r, 0, Math.PI * 2);
      ctx.fill();

      // 圆环
      ctx.strokeStyle = `${colors[signal.signalType] || '#E8C85A'}40`;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(sx, sy, r + 3, 0, Math.PI * 2);
      ctx.stroke();
    });

    // 中心点
    ctx.fillStyle = '#E8C85A';
    ctx.beginPath();
    ctx.arc(cx, cy, 4, 0, Math.PI * 2);
    ctx.fill();

  }, [rotation, signals]);

  return (
    <div style={{
      borderRadius: 20,
      background: 'rgba(255,255,255,0.015)',
      border: '1px solid rgba(255,255,255,0.04)',
      padding: '24px',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* 标题 */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 16,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 8, height: 8, borderRadius: '50%',
            background: '#E8C85A',
            boxShadow: '0 0 8px rgba(232,200,90,0.4)',
            animation: 'pulse 2s ease-in-out infinite',
          }} />
          <span style={{ fontSize: 15, fontWeight: 600, color: '#F3F5EF' }}>
            信号雷达
          </span>
          <span style={{ fontSize: 12, color: '#64766D' }}>
            {signals.length} 路信号激活
          </span>
        </div>
        <div style={{ display: 'flex', gap: 16 }}>
          {[
            { label: '天气', color: '#E8C85A' },
            { label: '节假日', color: '#69B89A' },
            { label: '事件', color: '#A68BD9' },
            { label: '趋势', color: '#D48BA0' },
          ].map(item => (
            <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <div style={{
                width: 6, height: 6, borderRadius: '50%', background: item.color,
              }} />
              <span style={{ fontSize: 11, color: '#64766D' }}>{item.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* 雷达画布 */}
      <canvas
        ref={canvasRef}
        style={{
          width: '100%',
          height: compact ? 220 : 280,
          display: 'block',
        }}
      />

      {/* 信号列表 */}
      {!compact && (
        <div style={{
          display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap',
        }}>
          {signals.slice(0, 6).map((signal, i) => (
            <SignalTag key={i} signal={signal} />
          ))}
        </div>
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>
    </div>
  );
}

function SignalTag({ signal }) {
  const colorMap = {
    WEATHER: '#E8C85A',
    HOLIDAY: '#69B89A',
    EVENT: '#A68BD9',
    CALENDAR: '#8EB9D6',
    SEARCH_TREND: '#D48BA0',
  };
  const color = colorMap[signal.signalType] || '#E8C85A';

  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '5px 12px', borderRadius: 12,
      background: `${color}08`,
      border: `1px solid ${color}15`,
      fontSize: 12, color: '#A9B8B0',
      transition: 'all 200ms ease',
    }}
      onMouseEnter={e => e.currentTarget.style.background = `${color}12`}
      onMouseLeave={e => e.currentTarget.style.background = `${color}08`}
    >
      <div style={{
        width: 5, height: 5, borderRadius: '50%', background: color,
        boxShadow: `0 0 4px ${color}60`,
      }} />
      <span>{signal.name}</span>
      <span style={{ color, fontWeight: 600 }}>{signal.intensity}</span>
    </div>
  );
}
